"use client";
import { useEffect } from "react";
import type { PermissionAssetType, Member, ShareRole } from "@zapier/bff";
import { HydratedAssetShare, useCreateAssetShare } from "@zapier/client";

import {
  AutoTabs,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  TabsItemType,
} from "@zapier/design-system";

import { useNotification } from "@zapier/utils";
import { AddShareForm } from "./AddShareForm";
import { ShareSubject } from "./AddShareTypeahead";
import { ShareList } from "./ShareList";
import { useTabs } from "./useTabs";

import styles from "./ShareModal.module.css";

interface OnShareCallbackParameters {
  subject: ShareSubject;
  role: ShareRole;
}

export interface ShareModalProps {
  /** ID of the asset being shared */
  assetId: string;
  /** Name of the asset being shared */
  assetName: string;
  /** Owner of the asset being shared */
  assetOwner?: Member;
  /** Type of the asset being shared */
  assetType: PermissionAssetType;

  /** If the asset is allowed to be shared with everyone on the account */
  allowSharingWithEveryone?: boolean;
  /** If the asset is allowed to be shared with teams */
  allowSharingWithTeams?: boolean;
  /** If the asset is allowed to be shared with admins */
  allowSharingWithAdmins?: boolean;
  /** If the asset is allowed to be shared */
  canShare?: boolean;
  /** Allowed roles for sharing the asset */
  roles?: ShareRole[];

  /** If the modal should be visible */
  isOpen?: boolean;
  /** Callback when the modal's visibility changes */
  onOpenChange?: (isOpen: boolean) => void;
  /** Callback when the asset is shared */
  onShare?: (share: HydratedAssetShare) => void;
  /** Callback when making someone the owner of the asset */
  onChangeOwner?: (newOwner: Member) => void;
  /** Callback when the current tab changes */
  onTabChange?: (tabKey: string) => void;

  /** Content to be displayed after the header but before the body */
  introContent?: React.ReactNode;
  /** Content to be displayed in the footer */
  footerContent?: React.ReactNode;
  /** The name of the default sharing tab */
  currentTabName?: string;
  emit?: (subjectId: string) => void;
  /** Additional tabs to be displayed in the modal (they will be placed after the sharing tab) */
  extraTabs?: Record<
    string,
    Omit<TabsItemType, "href"> & { body: React.ReactNode }
  >;
  canChangeOwner?: boolean;
}

type ShareModalBodyProps = Pick<
  ShareModalProps,
  | "allowSharingWithEveryone"
  | "allowSharingWithTeams"
  | "allowSharingWithAdmins"
  | "assetId"
  | "assetName"
  | "assetOwner"
  | "assetType"
  | "emit"
  | "introContent"
  | "onShare"
  | "onChangeOwner"
  | "roles"
  | "canShare"
  | "canChangeOwner"
>;

export function ShareModalBody({
  allowSharingWithEveryone = false,
  allowSharingWithTeams = false,
  allowSharingWithAdmins = false,
  assetId,
  assetName,
  assetOwner,
  assetType,
  emit,
  introContent,
  onShare,
  onChangeOwner,
  canChangeOwner,
  roles = ["editor", "viewer"],
  canShare = true,
}: ShareModalBodyProps) {
  const { createNotification } = useNotification();
  const { mutateAsync: createShare } = useCreateAssetShare();

  const handleOnShare = async ({
    role,
    subject,
  }: OnShareCallbackParameters) => {
    try {
      const createdShare = await createShare({
        assetId,
        assetType,
        subjectId: subject.id,
        subjectType: subject.type,
        role,
      });
      createNotification({
        type: "success",
        message: `Shared “${assetName}” with ${subject.name}.`,
      });
      onShare?.(createdShare);
      emit?.(subject.id);
    } catch (error) {
      createNotification({
        type: "error",
        message: `Failed to share “${assetName}” with ${subject.name}. The error was: ${error instanceof Error ? error.message : error}`,
      });
    }
  };

  return (
    <>
      {introContent}

      {canShare && (
        <AddShareForm
          allowSharingWithEveryone={allowSharingWithEveryone}
          allowSharingWithAdmins={allowSharingWithAdmins}
          allowSharingWithTeams={allowSharingWithTeams}
          assetId={assetId}
          assetType={assetType}
          disabledUserIds={assetOwner?.id ? [String(assetOwner.id)] : undefined}
          roles={roles}
          onShare={handleOnShare}
        />
      )}

      <div className={styles.shareList}>
        <ShareList
          isDisabled={!canShare}
          assetId={assetId}
          assetOwner={assetOwner}
          assetType={assetType}
          roles={roles}
          onChangeOwner={onChangeOwner}
          canChangeOwner={canChangeOwner}
        />
      </div>
    </>
  );
}

export function ShareModal({
  assetId,
  assetName,
  assetOwner,
  assetType,
  allowSharingWithEveryone,
  allowSharingWithTeams,
  allowSharingWithAdmins,
  canShare,
  roles,
  isOpen,
  onOpenChange,
  onShare,
  onTabChange,
  onChangeOwner,
  introContent,
  footerContent,
  currentTabName,
  emit,
  extraTabs,
  canChangeOwner,
}: ShareModalProps) {
  const header = `Share "${assetName}"`;

  const { activeTabBody, activeTabHref, activeTabKey, tabItems } = useTabs({
    share: {
      body: (
        <ShareModalBody
          allowSharingWithEveryone={allowSharingWithEveryone}
          allowSharingWithTeams={allowSharingWithTeams}
          allowSharingWithAdmins={allowSharingWithAdmins}
          assetId={assetId}
          assetName={assetName}
          assetOwner={assetOwner}
          assetType={assetType}
          emit={emit}
          introContent={introContent}
          onShare={onShare}
          onChangeOwner={onChangeOwner}
          roles={roles}
          canShare={canShare}
          canChangeOwner={canChangeOwner}
        />
      ),
      text: currentTabName || "Share",
    },
    ...extraTabs,
  });

  // Call onTabChange when the active tab changes
  useEffect(() => {
    if (onTabChange) {
      onTabChange(activeTabKey);
    }
  }, [activeTabKey, onTabChange]);

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalDialogHeader>
        <div className={styles.title}>{header}</div>

        {extraTabs && (
          <AutoTabs items={tabItems} selectedItemHref={activeTabHref} />
        )}
      </ModalDialogHeader>
      <ModalDialogBody>
        <div className={styles.contentWrapper}>{activeTabBody}</div>
      </ModalDialogBody>
      <ModalDialogFooter className={styles.footer}>
        {footerContent}
      </ModalDialogFooter>
    </ModalDialogRoot>
  );
}
